/**
 * Media API Service
 *
 * Handles interactions with restaurant media endpoints (images/videos)
 * Follows the same pattern as email.ts for consistency
 */
import { apiRequest } from './client';

/**
 * Restaurant Media interface
 * Matches the restaurant_media table schema
 */
export interface RestaurantMedia {
  id: string;
  organisation_id: string;
  restaurant_id?: string | null;

  // Media type
  media_type: 'image' | 'video';
  source_type: 'uploaded' | 'ai_generated';

  // AI generation config (for AI-generated media)
  ai_provider?: string | null;  // 'gemini', 'sora', null for uploaded
  ai_model?: string | null;
  ai_prompt?: string | null;
  ai_config?: Record<string, any> | null;
  ai_request_id?: string | null;

  // Storage
  storage_bucket: string;  // 'images' or 'videos'
  storage_path: string;
  public_url: string;
  video_url?: string;  // Backend returns this for video job status
  thumbnail_url?: string | null;

  // File metadata
  width?: number | null;
  height?: number | null;
  file_size?: number | null;
  mime_type?: string | null;
  original_filename?: string | null;

  // Flexible associations (JSONB)
  associations: Record<string, any>;
  tags: string[];

  // Status tracking
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  error_message?: string | null;
  retry_count?: number;

  // Audit
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

/**
 * Upload media parameters
 */
export interface UploadMediaParams {
  file: File;
  organisation_id: string;
  restaurant_id?: string | null;
  tags?: string[];
  associations?: Record<string, any>;
}

/**
 * AI image generation parameters
 */
export interface GenerateImageParams {
  mode: 'text-to-image' | 'reference-images';
  prompt: string;
  referenceSources?: Array<{ id: string; sourceType: string }>;
  organisation_id: string;
  restaurant_id?: string | null;
  imageConfig: {
    aspectRatio: string;  // e.g., '16:9', '1:1', '9:16'
  };
  filename?: string;  // Optional custom filename for the generated image
  associations?: Record<string, any>;
  tags?: string[];
}

/**
 * AI video generation parameters
 */
export interface GenerateVideoParams {
  mode: 'image-to-video' | 'text-to-video' | 'generated-image-to-video';
  prompt: string;
  imagePrompt?: string;  // For generated-image-to-video mode
  inputSource?: { imageId: string; sourceType: string };  // For image-to-video mode
  organisation_id: string;
  restaurant_id?: string | null;
  soraModel?: 'sora-2' | 'sora-2-pro';
  videoConfig?: {
    size: string;  // '1280x720', '1920x1080', etc.
    seconds: number;  // 4, 8, or 12
  };
  filename?: string;  // Optional custom filename for the generated video
  associations?: Record<string, any>;
  tags?: string[];
}

/**
 * List media response interface
 */
interface ListMediaResponse {
  success: boolean;
  images?: RestaurantMedia[];  // For GET /api/media/images
  videos?: RestaurantMedia[];  // For GET /api/media/videos
  requestId?: string;
}

/**
 * Single media response interface
 */
interface SingleMediaResponse {
  success: boolean;
  job: RestaurantMedia;  // Backend returns "job" for both images and videos
  message?: string;
  requestId?: string;
}

/**
 * Generic API response interface
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  requestId?: string;
}

/**
 * Media Service for interacting with media management APIs
 */
const MediaService = {
  /**
   * ==================== IMAGE ENDPOINTS ====================
   */

  /**
   * Fetch images for an organisation
   */
  fetchImages: async (
    organisationId: string,
    filters?: {
      restaurant_id?: string;
      status?: string;
      source_type?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<RestaurantMedia[]> => {
    try {
      console.log('[Media Images] Fetching images for organisation:', organisationId, 'filters:', filters);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (filters?.restaurant_id) params.append('restaurant_id', filters.restaurant_id);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.source_type) params.append('source_type', filters.source_type);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiRequest<ListMediaResponse>({
        method: 'GET',
        url: `/api/media/images?${params.toString()}`
      });

      if (response && response.success && response.images) {
        console.log(`[Media Images] Successfully fetched ${response.images.length} images`);
        return response.images;
      }

      throw new Error('Failed to fetch images');
    } catch (error) {
      console.error('[Media Images] Error fetching images:', error);
      throw error;
    }
  },

  /**
   * Get a single image by ID
   */
  getImage: async (imageId: string): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Images] Fetching image:', imageId);

      const response = await apiRequest<SingleMediaResponse>({
        method: 'GET',
        url: `/api/media/images/${imageId}`
      });

      if (response && response.success && response.job) {
        console.log('[Media Images] Successfully fetched image');
        return response.job;
      }

      throw new Error('Failed to fetch image');
    } catch (error) {
      console.error('[Media Images] Error fetching image:', error);
      throw error;
    }
  },

  /**
   * Upload an image
   * Note: Uses native fetch instead of apiRequest because we need to send FormData
   */
  uploadImage: async (params: UploadMediaParams): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Images] Uploading image:', params.file.name);

      // Validate required fields
      if (!params.organisation_id) {
        throw new Error('Organisation ID is required');
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', params.file);
      formData.append('organisation_id', params.organisation_id);

      if (params.restaurant_id) {
        formData.append('restaurant_id', params.restaurant_id);
      }

      if (params.tags && params.tags.length > 0) {
        formData.append('tags', JSON.stringify(params.tags));
      }

      if (params.associations) {
        formData.append('associations', JSON.stringify(params.associations));
      }

      // Get auth token from Supabase
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      // Use native fetch for file upload (apiRequest doesn't support FormData)
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pumpd-webhook-app-5c7ade204a3d.herokuapp.com';
      const response = await fetch(`${API_BASE_URL}/api/media/images/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to upload image');
      }

      if (data && data.success && data.job) {
        console.log('[Media Images] Image uploaded successfully:', data.job.id);
        return data.job;
      }

      throw new Error(data?.message || 'Failed to upload image');
    } catch (error) {
      console.error('[Media Images] Error uploading image:', error);
      throw error;
    }
  },

  /**
   * Generate an image using AI
   */
  generateImage: async (params: GenerateImageParams): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Images] Generating image with AI:', params.mode);

      // Validate required fields
      if (!params.organisation_id) {
        throw new Error('Organisation ID is required');
      }
      if (!params.prompt) {
        throw new Error('Prompt is required');
      }
      if (!params.imageConfig?.aspectRatio) {
        throw new Error('Image config with aspect ratio is required');
      }

      const response = await apiRequest<SingleMediaResponse>({
        method: 'POST',
        url: '/api/media/images/generate',
        data: {
          mode: params.mode,
          prompt: params.prompt,
          referenceSources: params.referenceSources,
          organisation_id: params.organisation_id,
          restaurant_id: params.restaurant_id,
          imageConfig: params.imageConfig,
          filename: params.filename,
          associations: params.associations || {},
          tags: params.tags || []
        }
      });

      if (response && response.success && response.job) {
        console.log('[Media Images] Image generated successfully:', response.job.id);
        return response.job;
      }

      throw new Error(response?.message || 'Failed to generate image');
    } catch (error) {
      console.error('[Media Images] Error generating image:', error);
      throw error;
    }
  },

  /**
   * Delete an image
   */
  deleteImage: async (imageId: string): Promise<void> => {
    try {
      console.log('[Media Images] Deleting image:', imageId);

      const response = await apiRequest<ApiResponse>({
        method: 'DELETE',
        url: `/api/media/images/${imageId}`
      });

      if (response && response.success) {
        console.log('[Media Images] Image deleted successfully');
        return;
      }

      throw new Error(response?.message || 'Failed to delete image');
    } catch (error) {
      console.error('[Media Images] Error deleting image:', error);
      throw error;
    }
  },

  /**
   * ==================== VIDEO ENDPOINTS ====================
   */

  /**
   * Fetch videos for an organisation
   */
  fetchVideos: async (
    organisationId: string,
    filters?: {
      restaurant_id?: string;
      status?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<RestaurantMedia[]> => {
    try {
      console.log('[Media Videos] Fetching videos for organisation:', organisationId);

      const params = new URLSearchParams({
        organisation_id: organisationId,
      });

      if (filters?.restaurant_id) params.append('restaurant_id', filters.restaurant_id);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiRequest<ListMediaResponse>({
        method: 'GET',
        url: `/api/media/videos?${params.toString()}`
      });

      if (response && response.success && response.videos) {
        console.log(`[Media Videos] Successfully fetched ${response.videos.length} videos`);
        return response.videos;
      }

      throw new Error('Failed to fetch videos');
    } catch (error) {
      console.error('[Media Videos] Error fetching videos:', error);
      throw error;
    }
  },

  /**
   * Get a single video by ID
   */
  getVideo: async (videoId: string): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Videos] Fetching video:', videoId);

      const response = await apiRequest<SingleMediaResponse>({
        method: 'GET',
        url: `/api/media/videos/${videoId}`
      });

      if (response && response.success && response.job) {
        console.log('[Media Videos] Successfully fetched video');
        return response.job;
      }

      throw new Error('Failed to fetch video');
    } catch (error) {
      console.error('[Media Videos] Error fetching video:', error);
      throw error;
    }
  },

  /**
   * Generate a video using AI
   */
  generateVideo: async (params: GenerateVideoParams): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Videos] Generating video with AI:', params.mode);

      // Validate required fields
      if (!params.organisation_id) {
        throw new Error('Organisation ID is required');
      }
      if (!params.prompt) {
        throw new Error('Prompt is required');
      }

      const response = await apiRequest<SingleMediaResponse>({
        method: 'POST',
        url: '/api/media/videos/generate',
        data: {
          mode: params.mode,
          prompt: params.prompt,
          imagePrompt: params.imagePrompt,
          inputSource: params.inputSource,
          organisation_id: params.organisation_id,
          restaurant_id: params.restaurant_id,
          soraModel: params.soraModel || 'sora-2',
          videoConfig: params.videoConfig || { size: '1280x720', seconds: 8 },
          filename: params.filename,
          associations: params.associations || {},
          tags: params.tags || []
        }
      });

      if (response && response.success && response.job) {
        console.log('[Media Videos] Video generation started:', response.job.id);
        return response.job;
      }

      throw new Error(response?.message || 'Failed to generate video');
    } catch (error) {
      console.error('[Media Videos] Error generating video:', error);
      throw error;
    }
  },

  /**
   * Fetch job status for a video generation job
   * Used for polling to check if video generation is complete
   */
  fetchJobStatus: async (jobId: string): Promise<RestaurantMedia> => {
    try {
      console.log('[Media Videos] Fetching job status:', jobId);

      const response = await apiRequest<SingleMediaResponse>({
        method: 'GET',
        url: `/api/media/videos/${jobId}`
      });

      if (response && response.success && response.job) {
        console.log('[Media Videos] Job status:', response.job.status);
        return response.job;
      }

      throw new Error('Failed to fetch job status');
    } catch (error) {
      console.error('[Media Videos] Error fetching job status:', error);
      throw error;
    }
  },

  /**
   * Delete a video
   */
  deleteVideo: async (videoId: string): Promise<void> => {
    try {
      console.log('[Media Videos] Deleting video:', videoId);

      const response = await apiRequest<ApiResponse>({
        method: 'DELETE',
        url: `/api/media/videos/${videoId}`
      });

      if (response && response.success) {
        console.log('[Media Videos] Video deleted successfully');
        return;
      }

      throw new Error(response?.message || 'Failed to delete video');
    } catch (error) {
      console.error('[Media Videos] Error deleting video:', error);
      throw error;
    }
  },
};

export default MediaService;
